/* Combined-listing variant switch: stop the base theme blanking the image gallery.
 *
 * On a combined listing, picking another option value loads a different PRODUCT, so
 * `variant-picker.js` takes its `morphElementSelector = 'main'` branch: it fetches the
 * child product's page, parses it, and morphs the whole `<main>` from it. `morph.js`
 * MOVES nodes out of that parsed document into the live DOM, so once the morph has run
 * the parsed document has been cannibalised.
 *
 * It is then handed to every `variant:update` subscriber anyway, and
 * `media-gallery.js` does:
 *
 *     const newMediaGallery = source.querySelector('media-gallery');
 *     if (!newMediaGallery) return;
 *     this.replaceWith(newMediaGallery);
 *
 * `<media-gallery>` carries no `id`, so morph matched it on tag name and updated it in
 * place rather than moving it — which leaves a findable but EMPTY shell behind in the
 * parsed document. The line above then installs that shell over the gallery the morph
 * had just filled in correctly. Measured on the live store, at dispatch time:
 *
 *     parsed-doc media-gallery: 0 slides, innerHTML 1524
 *     live media-gallery:       2 slides, innerHTML 18613   <- correct, about to be lost
 *
 * The gallery is the only casualty: components that morph moved out (`product-sku`,
 * `local-pickup`) fail their own `querySelector` and no-op, and ones that morph rather
 * than replace (`product-price`, `product-inventory`) survive. Only `media-gallery`
 * both survives lookup and replaces destructively.
 *
 * Symptom: switching from a one-image child to a multi-image child empties the gallery
 * until a reload. The other direction empties the zoom dialog instead, so opening zoom
 * throws `Required ref "media" not found in component zoom-dialog`.
 *
 * This is a base-theme defect, not one of ours — it reproduces on stock Horizon with
 * none of this project's code loaded. Fixed from the outside so no vendor file is
 * forked (AGENTS.md section 3): drop the spent `<media-gallery>` from the parsed
 * document before the vendor listener looks for it, so its own early return fires and
 * the live gallery is left alone. If a future Horizon release fixes `media-gallery.js`,
 * this becomes a no-op rather than a merge conflict.
 */
(function () {
  'use strict';

  /* `variant:update` is dispatched on the `<variant-picker>` and `media-gallery.js`
   * listens on the enclosing `.shopify-section` — in the BUBBLE phase. Capture runs
   * document-first, so a capture listener here always gets there before it. */
  var CAPTURE = true;

  /* Set on `variant:selected` and read on the `variant:update` that follows it. */
  var loadsNewProduct = false;
  var previousProductId = null;

  /**
   * Does this selection load a different product — i.e. will `variant-picker.js` morph
   * the whole of `<main>` and spend the parsed document doing it?
   *
   * Deliberately the same test as `variantChanged()` makes for its `morphElementSelector`,
   * and it has to be evaluated HERE, on `variant:selected`, because that event is
   * dispatched synchronously before the fetch starts while `data-product-url` still
   * names the outgoing product.
   *
   * The two payload-based shortcuts both fail, so don't reach for them again:
   * `detail.data.productId` is already the NEW id by the time `variant:update` fires
   * (the morph rewrote the picker's `data-product-id`), and testing the parsed gallery
   * for `.product-media` misses the multi-to-single direction.
   *
   * @param {Element} picker
   * @returns {boolean}
   */
  function selectionLoadsNewProduct(picker) {
    if (picker.dataset.templateProductMatch !== 'true') return false;

    var selected = picker.querySelector('fieldset input:checked, select option[selected]');
    if (!selected) return false;

    var newUrl = selected.dataset.connectedProductUrl;
    var currentUrl = (picker.dataset.productUrl || '').split('?')[0];

    return Boolean(newUrl) && newUrl !== currentUrl;
  }

  function onVariantSelected(event) {
    var picker = event.target;
    if (!(picker instanceof HTMLElement) || picker.tagName !== 'VARIANT-PICKER') return;

    loadsNewProduct = selectionLoadsNewProduct(picker);
    previousProductId = picker.dataset.productId || null;
  }

  function onVariantUpdate(event) {
    if (!loadsNewProduct) return;
    /* One flag per selection: clear it even if the payload turns out unusable, so a
     * later non-combined update on the same page cannot inherit it. */
    loadsNewProduct = false;

    var data = event.detail && event.detail.data;
    if (!data || !data.html) return;

    /* The fix. `media-gallery.js` early-returns when this is absent, leaving the live
     * gallery — already correct, courtesy of the morph — exactly as it is. */
    var spentGallery = data.html.querySelector('media-gallery');
    if (spentGallery) spentGallery.remove();

    /* While we are here: `variant-picker.js` only ever assigns `data.newProduct` in its
     * `updateVariantPicker()` branch, which is the NON-combined one, so it is always
     * undefined on precisely the combined-listing path `events.js` documents it for.
     * Six subscribers read it, ours included (`ld-store-availability.js`). Nothing is
     * visibly broken today — the `<main>` morph rewrites their `data-*` attributes
     * wholesale, so they land on correct values by luck rather than by contract — but
     * with the flag supplied they take the branch that was written for them.
     *
     * `event.detail` is a plain object (`events.js`), so this is visible to every
     * bubble-phase listener that runs after us. `data.productId` is deliberately left
     * alone: those subscribers ignore it once `newProduct` is set. */
    var source = data.html.querySelector('variant-picker[data-product-id]');
    if (!source || !previousProductId) return;
    if (source.dataset.productId === previousProductId) return;

    data.newProduct = {
      id: source.dataset.productId,
      url: source.dataset.productUrl,
    };
  }

  document.addEventListener('variant:selected', onVariantSelected, CAPTURE);
  document.addEventListener('variant:update', onVariantUpdate, CAPTURE);
})();
